import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { Eye, UserPen, Trash2 } from 'lucide-react';
import { NameEmailAvatar } from '../../../ui';
import { TableColumn } from '../../../ui/components/Common/DataTable/Table';
import {
  ContextMenu,
  MenuItemType,
} from '../../../ui/components/Common/ContextMenu/ContextMenu';
import { ContextMenuButton } from '../../../ui/components/Common/ContextMenu/ContextMenuButton';
import { TooltipWrapper } from '../../job_openings/view/Functions';
import type { IOrganizationTableRow } from '../interfaces';

export interface IOrganizationMenuHandlers {
  onView: (org: IOrganizationTableRow) => void;
  onEdit: (org: IOrganizationTableRow) => void;
  onDelete: (org: IOrganizationTableRow) => void;
}

export const getOrganizationMenuItems = (
  handlers: IOrganizationMenuHandlers,
): MenuItemType<IOrganizationTableRow>[] => [
  {
    label: 'View Details',
    icon: <Eye size={16} />,
    permission: [],
    onClick: (row) => handlers.onView(row),
  },
  {
    label: 'Edit Organization',
    icon: <UserPen size={16} />,
    permission: [],
    onClick: (row) => handlers.onEdit(row),
  },
  {
    label: 'Delete',
    icon: <Trash2 size={16} color="#f43f5e" />,
    permission: [],
    onClick: (row) => handlers.onDelete(row),
  },
];

const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (status.toLowerCase()) {
    case 'active':
      return 'success';
    case 'trial':
    case 'pending':
      return 'warning';
    case 'cancelled':
    case 'expired':
      return 'error';
    default:
      return 'default';
  }
};

export const getOrganizationColumns = (
  onOpenMenu: (e: React.MouseEvent<HTMLButtonElement>, row: unknown) => void,
  onCloseMenu: () => void,
  anchorEl: HTMLElement | null,
): TableColumn<IOrganizationTableRow>[] => [
  {
    key: 'organization',
    label: 'Organization',
    width: '240px',
    align: 'left',
    render: (row) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <NameEmailAvatar
          avatar={row.source.organization.companyLogo ?? ''}
          name={row.source.organization.organizationName}
          email={row.source.organization.primaryDomain}
        />
      </Box>
    ),
  },
  {
    key: 'primaryDomain',
    label: 'Primary Domain',
    width: '180px',
    align: 'left',
    render: (row) => <TooltipWrapper text={row.source.organization.primaryDomain} />,
  },
  {
    key: 'companySize',
    label: 'Company Size',
    align: 'left',
    width: '140px',
    render: (row) => <TooltipWrapper text={row.source.subscription.companySize} />,
  },
  {
    key: 'subscription',
    label: 'Subscription',
    align: 'left',
    width: '140px',
    render: (row) => {
      const status = row.source.subscription.candidateFeature?.status ?? 'unknown';
      return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', textAlign: 'left' }}>
          <Chip
            label={status}
            size="small"
            color={getStatusColor(status)}
            sx={{
              fontWeight: 700,
              fontSize: '0.6875rem',
              borderRadius: 2,
              height: 22,
            }}
          />
        </Box>
      );
    },
  },
  {
    key: 'actions',
    label: 'Actions',
    width: '80px',
    align: 'right',
    render: (row) => (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pr: 1 }}>
        <ContextMenuButton
          row={row}
          onOpenMenu={onOpenMenu}
          onCloseMenu={onCloseMenu}
          anchorEl={anchorEl}
        />
      </Box>
    ),
  },
];

export interface IOrganizationRowMenuProps {
  anchorEl: HTMLElement | null;
  selectedRow: IOrganizationTableRow | null;
  menuItems: MenuItemType<IOrganizationTableRow>[];
  onClose: () => void;
}

export const OrganizationRowMenu: React.FC<IOrganizationRowMenuProps> = ({
  anchorEl,
  selectedRow,
  menuItems,
  onClose,
}) => {
  if (!anchorEl || !selectedRow) return null;
  return (
    <ContextMenu
      anchorEl={anchorEl}
      onClose={onClose}
      row={selectedRow}
      menuItems={menuItems}
    />
  );
};
